import React, { createContext, useContext, useMemo } from "react";
import {
  loadDoctorConsoleConfig,
  getVisibleSections,
  getVisibleHistorySubSections,
  getSectionByKey,
  buildRuleContext,
  isFeatureEnabled,
  getFeatureConfig,
} from "./engine/loadConfig";
import { resolveSectionDependencies } from "./engine/resolveDependencies";

const DoctorConsoleConfigContext = createContext(null);

export const DoctorConsoleConfigProvider = ({
  children,
  config: configOverride = null,
  patient = {},
  permissions = {},
  appointment = {},
  doctor = {},
  stores = {},
}) => {
  const config = useMemo(() => loadDoctorConsoleConfig(configOverride), [configOverride]);

  const ruleContext = useMemo(
    () => buildRuleContext({ patient, permissions, appointment, doctor, stores }),
    [patient, permissions, appointment, doctor, stores],
  );

  const visibleSections = useMemo(
    () => getVisibleSections(config, ruleContext),
    [config, ruleContext],
  );

  const historySectionConfig = useMemo(
    () => getSectionByKey(config, "History"),
    [config],
  );

  const visibleHistorySubSections = useMemo(
    () => getVisibleHistorySubSections(historySectionConfig, ruleContext),
    [historySectionConfig, ruleContext],
  );

  const value = useMemo(
    () => ({
      config,
      ruleContext,
      visibleSections,
      historySectionConfig,
      visibleHistorySubSections,
      getSectionConfig: (key) => getSectionByKey(config, key),
      getFeatureConfig: (featureId) => getFeatureConfig(config, featureId),
      isFeatureEnabled: (featureId) => isFeatureEnabled(config, featureId, ruleContext),
      resolveSectionDependencies: (sectionConfig) =>
        resolveSectionDependencies(sectionConfig, config, stores),
    }),
    [
      config,
      ruleContext,
      visibleSections,
      historySectionConfig,
      visibleHistorySubSections,
      stores,
    ],
  );

  return (
    <DoctorConsoleConfigContext.Provider value={value}>
      {children}
    </DoctorConsoleConfigContext.Provider>
  );
};

export const useDoctorConsoleConfig = () => {
  const ctx = useContext(DoctorConsoleConfigContext);
  if (!ctx) {
    throw new Error("useDoctorConsoleConfig must be used within DoctorConsoleConfigProvider");
  }
  return ctx;
};
